import _ from 'lodash';

import { ExtensionType } from 'constants/loanApplication';
import {
  HistoryTypeToPendingChangesTypeMap,
  PendingChangesActionType,
} from 'constants/pendingChanges';
import { getConditionalObject } from 'utils/data';
import { formatNumberToFixed3Decimals, getNumber } from 'utils/number';

import {
  CustomAdjustmentType,
  OPTIMAL_BLUE_ADJUSTMENT_PRICE_PREFIX,
  optimalBlueAdjustmentTypesWithPricePrefix,
} from './LockInfo.constants';

export const getTotalAmount = adjustments => {
  const filteredAdjustments = adjustments.filter(
    ({ pendingChangesActionType }) => pendingChangesActionType !== PendingChangesActionType.DELETED,
  );

  return formatNumberToFixed3Decimals(
    _.sumBy(filteredAdjustments, ({ amount }) => {
      return getNumber(amount);
    }),
  );
};

export const prepareTableData = (data, pendingChangesData) => {
  return data.map(valuesData => {
    const shouldAddPricePrefix = valuesData.isExternal;
    const originalAdjustmentType = valuesData.originalType;

    const prefix = optimalBlueAdjustmentTypesWithPricePrefix.includes(originalAdjustmentType)
      ? OPTIMAL_BLUE_ADJUSTMENT_PRICE_PREFIX
      : originalAdjustmentType;

    const adjustmentName =
      shouldAddPricePrefix && valuesData.name && prefix
        ? `${prefix} ${valuesData.name}`
        : valuesData.name;

    const values = {
      ...valuesData,
      amount: formatNumberToFixed3Decimals(valuesData.amount),
      name: adjustmentName,
    };
    const pendingChanges = pendingChangesData.adjustments?.[values.id];

    if (!pendingChanges) {
      return {
        ...values,
        pendingChangesActionType: PendingChangesActionType.NO_CHANGES,
      };
    }

    const modifiedFields = Object.keys(pendingChanges);

    const normalizedModifiedFields = modifiedFields.reduce((acc, field) => {
      const pendingChange = pendingChanges[field];

      if (pendingChange && pendingChange.newValue === pendingChange.initialValue) {
        return acc;
      }

      acc.push(_.upperFirst(field));

      return acc;
    }, []);

    const historyActionType = modifiedFields[0]
      ? pendingChanges[modifiedFields[0]]?.actionType
      : '';

    const pendingChangesActionType = HistoryTypeToPendingChangesTypeMap[historyActionType];

    return {
      ...values,
      modifiedFields: normalizedModifiedFields,
      pendingChangesActionType,
    };
  });
};

export const getLockExtensionName = ({ item: { term, type }, isExternal }) => {
  switch (type) {
    case ExtensionType.REPRICE_LOAN:
      return `${term} Reprice Loan Lock Extension`;

    case ExtensionType.RETAIN_RATE:
      return `${term} Retain Rate Lock Extension`;

    default:
      return `${isExternal ? `Price ${term} Day` : `${term} Rate`} Lock Extension`;
  }
};

const createNewTableData = type => {
  return {
    id: _.uniqueId(),
    isAdded: true,
    type,
  };
};

export const prepareFormData = ({ form, type, cost, isExternal = false }) => {
  const dataToSave = createNewTableData(type);

  switch (type) {
    case CustomAdjustmentType.CUSTOM_ADJUSTMENT:
      return {
        ...dataToSave,
        name: form.adjustmentReason.name,
        amount: form.adjustmentAmount,
        customAdjustmentTypeId: form.adjustmentReason.value,
        ...getConditionalObject(isExternal, {
          comment: form.adjustmentComment,
          isExternal,
        }),
      };

    case CustomAdjustmentType.EXTENSION:
      return {
        ...dataToSave,
        ...form,
        name: getLockExtensionName({
          item: { term: form.term, type: form.lockExtensionType },
          isExternal,
        }),
      };

    case CustomAdjustmentType.FLOAT_DOWN:
      return {
        ...dataToSave,
        amount: cost,
        name: 'Float Down',
      };

    default:
      break;
  }
};

export const removeSecondsFromRateSheetTitle = title => {
  if (!title) {
    return null;
  }

  const [date, time, meridiem] = title.split(' ');
  const [hours, minutes] = time.split(':');

  return `${date} ${hours}:${minutes} ${meridiem}`;
};
